import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../../services/apiClient";
import { ReGenerateToken } from "../auth/authSlice";

// PLM Hub Connector API v1. apiClient's baseURL is REACT_APP_apiBaseUrl
// (https://api.projectw.ai) and it attaches the Bearer token automatically.
const PLM_HUB_BASE = "/plm-hub/api/v1";

export const DEFAULT_GRANTS = {
  PLM_LEARNING: false,
  AGENT_READ: false,
  SPECIAL_CATEGORY: false,
};

const unwrapResponse = (response) => response?.data?.data ?? response?.data ?? response;

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong";

const servicePath = (service) => `${PLM_HUB_BASE}/connections/${encodeURIComponent(service)}`;

// Expired/invalid access token: HTTP 401, or the PLM Hub's
// { code: "NOT_PERMITTED", details: { reason: "BEARER_INVALID" } } body.
const isAuthError = (error) => {
  const data = error?.response?.data;
  return (
    error?.response?.status === 401 ||
    data?.details?.reason === "BEARER_INVALID" ||
    data?.details?.kind === "AUTHENTICATION_REQUIRED" ||
    data?.code === "NOT_PERMITTED"
  );
};

// On an auth error, refresh the token via ReGenerateToken (same as authSlice)
// and retry the thunk once. `_retried` stops an endless refresh loop.
const handleError = async (error, thunk, arg, { dispatch, rejectWithValue }) => {
  if (isAuthError(error) && !arg?._retried) {
    const action = await dispatch(ReGenerateToken(thunk, { ...arg, _retried: true }, dispatch));
    if (action?.meta?.requestStatus === "fulfilled") return action.payload;
    return rejectWithValue(action?.payload || getErrorMessage(error));
  }
  return rejectWithValue(getErrorMessage(error));
};

// 1. GET /connections — one status row per service.
export const fetchPlmConnections = createAsyncThunk(
  "plmHub/fetchConnections",
  async (arg, thunkApi) => {
    try {
      const response = await apiClient.get(`${PLM_HUB_BASE}/connections`);
      return unwrapResponse(response);
    } catch (error) {
      return handleError(error, fetchPlmConnections, arg, thunkApi);
    }
  }
);

// POST /connections/sync — confirms delegated services (Google, Instagram) and
// returns { connections, sync }. Call after returning from OAuth or on refresh.
export const syncPlmConnections = createAsyncThunk(
  "plmHub/syncConnections",
  async (arg, thunkApi) => {
    try {
      const response = await apiClient.post(`${PLM_HUB_BASE}/connections/sync`, null, {
        // No body: drop apiClient's default JSON Content-Type, otherwise the
        // backend tries to parse an empty body and returns ENTITY_PARSE_FAILED.
        headers: {
          "Content-Type": false,
          "X-Correlation-Id": `web-${Date.now()}`,
        },
      });
      return unwrapResponse(response);
    } catch (error) {
      return handleError(error, syncPlmConnections, arg, thunkApi);
    }
  }
);

// GET /connections/{service}/grants — the user's saved consent choices.
export const fetchPlmGrants = createAsyncThunk(
  "plmHub/fetchGrants",
  async (arg, thunkApi) => {
    const { service } = arg;
    try {
      const response = await apiClient.get(`${servicePath(service)}/grants`);
      const data = unwrapResponse(response);
      return { service, grants: { ...DEFAULT_GRANTS, ...(data?.grants || data) } };
    } catch (error) {
      return handleError(error, fetchPlmGrants, arg, thunkApi);
    }
  }
);

// 2. PUT /connections/{service}/grants — save consent choices.
export const savePlmGrants = createAsyncThunk(
  "plmHub/saveGrants",
  async (arg, thunkApi) => {
    const { service, grants } = arg;
    try {
      const response = await apiClient.put(`${servicePath(service)}/grants`, { grants });
      return { service, grants, data: unwrapResponse(response) };
    } catch (error) {
      return handleError(error, savePlmGrants, arg, thunkApi);
    }
  }
);

// 3. POST /connections/{service}/authorize — begin OAuth. Grants must match
// the ones just saved. client_return_uri is only needed for connector-owned
// providers such as Facebook.
export const authorizePlmConnection = createAsyncThunk(
  "plmHub/authorize",
  async (arg, thunkApi) => {
    const { service, grants, clientReturnUri } = arg;
    try {
      const body = { grants };
      if (clientReturnUri) body.client_return_uri = clientReturnUri;
      const response = await apiClient.post(`${servicePath(service)}/authorize`, body);
      return { service, data: unwrapResponse(response) };
    } catch (error) {
      return handleError(error, authorizePlmConnection, arg, thunkApi);
    }
  }
);

// Save grants, then authorize with the exact same grant set.
export const connectPlmService = createAsyncThunk(
  "plmHub/connect",
  async ({ service, grants = DEFAULT_GRANTS, clientReturnUri }, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(savePlmGrants({ service, grants })).unwrap();
      return await dispatch(authorizePlmConnection({ service, grants, clientReturnUri })).unwrap();
    } catch (error) {
      return rejectWithValue(typeof error === "string" ? error : getErrorMessage(error));
    }
  }
);

// 5. DELETE /connections/{service}. For Google the response may carry a
// non-null reauth_url; the UI ignores it and never redirects on disconnect.
export const disconnectPlmConnection = createAsyncThunk(
  "plmHub/disconnect",
  async (arg, thunkApi) => {
    const { service } = arg;
    try {
      const response = await apiClient.delete(servicePath(service));
      return { service, data: unwrapResponse(response) };
    } catch (error) {
      return handleError(error, disconnectPlmConnection, arg, thunkApi);
    }
  }
);

// OAuth URL to open — only when the backend says authorization is required.
export const getAuthorizeUrl = (data) =>
  data?.authorization_required ? data?.authorization_url || null : null;

const toConnectionList = (payload) =>
  Array.isArray(payload) ? payload : payload?.connections || [];

const initialState = {
  connections: { data: [], loading: false, error: null },
  sync: { data: null, loading: false, error: null },
  grants: { byService: {}, loading: false, error: null },
  authorize: { service: null, data: null, loading: false, error: null },
  disconnect: { service: null, data: null, loading: false, error: null },
};

const plmHubSlice = createSlice({
  name: "plmHub",
  initialState,
  reducers: {
    resetPlmHubState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlmConnections.pending, (state) => {
        state.connections.loading = true;
        state.connections.error = null;
      })
      .addCase(fetchPlmConnections.fulfilled, (state, action) => {
        state.connections.loading = false;
        state.connections.data = toConnectionList(action.payload);
      })
      .addCase(fetchPlmConnections.rejected, (state, action) => {
        state.connections.loading = false;
        state.connections.error = action.payload;
      })

      .addCase(syncPlmConnections.pending, (state) => {
        state.sync.loading = true;
        state.sync.error = null;
      })
      .addCase(syncPlmConnections.fulfilled, (state, action) => {
        state.sync.loading = false;
        state.sync.data = action.payload?.sync ?? null;
        state.connections.data = toConnectionList(action.payload);
      })
      .addCase(syncPlmConnections.rejected, (state, action) => {
        state.sync.loading = false;
        state.sync.error = action.payload;
      })

      .addCase(fetchPlmGrants.pending, (state) => {
        state.grants.loading = true;
        state.grants.error = null;
      })
      .addCase(fetchPlmGrants.fulfilled, (state, action) => {
        state.grants.loading = false;
        state.grants.byService[action.payload.service] = action.payload.grants;
      })
      .addCase(fetchPlmGrants.rejected, (state, action) => {
        state.grants.loading = false;
        state.grants.error = action.payload;
      })

      .addCase(savePlmGrants.pending, (state) => {
        state.grants.loading = true;
        state.grants.error = null;
      })
      .addCase(savePlmGrants.fulfilled, (state, action) => {
        state.grants.loading = false;
        state.grants.byService[action.payload.service] = action.payload.grants;
      })
      .addCase(savePlmGrants.rejected, (state, action) => {
        state.grants.loading = false;
        state.grants.error = action.payload;
      })

      .addCase(authorizePlmConnection.pending, (state, action) => {
        state.authorize.loading = true;
        state.authorize.error = null;
        state.authorize.service = action.meta.arg.service;
      })
      .addCase(authorizePlmConnection.fulfilled, (state, action) => {
        state.authorize.loading = false;
        state.authorize.data = action.payload.data;
      })
      .addCase(authorizePlmConnection.rejected, (state, action) => {
        state.authorize.loading = false;
        state.authorize.error = action.payload;
      })

      .addCase(disconnectPlmConnection.pending, (state, action) => {
        state.disconnect.loading = true;
        state.disconnect.error = null;
        state.disconnect.service = action.meta.arg.service;
      })
      .addCase(disconnectPlmConnection.fulfilled, (state, action) => {
        state.disconnect.loading = false;
        state.disconnect.data = action.payload.data;
      })
      .addCase(disconnectPlmConnection.rejected, (state, action) => {
        state.disconnect.loading = false;
        state.disconnect.error = action.payload;
      });
  },
});

// Map of service -> true for every service currently CONNECTED.
export const selectConnectedServices = (state) =>
  (state.plmHub?.connections?.data || []).reduce((acc, row) => {
    if ((row?.connection_state || row?.status) === "CONNECTED") acc[row.service] = true;
    return acc;
  }, {});

export const { resetPlmHubState } = plmHubSlice.actions;
export default plmHubSlice.reducer;
